import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Header from "./Header";
import { useQuestion } from "../../../context/QuestionsContext";
import { useAttemptedQuestions } from "../../../context/AttemptedQuestionsContext";
import QuestionSection from "./QuestionSection";
import QuestionSidebar from "./QuestionSidebar";
import CollapseToggle from "../iit/CollapseToggle";
import useGateTestStyles from "../testStyle/gateStyle/gateTestStyle"; // Import the styles
import SubmitSummary from "./SubmitSummary";
import socket from "../../../socket/socket";
import Loader from "../../../components/Loader";

function GateTest() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [currentSubject, setCurrentSubject] = useState([0]);
  const [currentSection, setCurrentSection] = useState([]);
  const [subjectId, setSubjectId] = useState(0);
  const [sectionId, setSectionId] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeSubjectId, setActiveSubjectId] = useState(null);
  const [sscTimeUp, setSscTimeUp] = useState(
    !!sessionStorage.getItem("submitByH") || !!sessionStorage.getItem("isTimeUp")
  );
  const [isTimerInitialized, setIsTimerInitialized] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const classes = useGateTestStyles(); // Use the styles

  const { fetchAttemptedQuestions } = useAttemptedQuestions();
  const { question } = useQuestion();

  // Flatten all questions from all subjects and sections while preserving section info
  const { questionData, questionToSectionMap, subjectNames, sectionNames } =
    useMemo(() => {
      const questions = [];
      const questionMap = {};

      const subjects = question?.subjects?.map((subject) => ({
        name: subject.subject_name,
        subject_id: subject.id,
      }));

      // setActiveSubjectId(subjects[0]?.subject_id)
      const sections = [];
      let subSections = [];

      question?.subjects?.forEach((subject) => {
        subject?.sections?.forEach((section) => {
          subSections.push({
            name: section.section_name,
            section_id: section.id,
          });
        });
        sections.push(subSections);
        subSections = [];
      });

      // setActiveSectionId(sections[0][0]?.section_id)

      question?.subjects?.forEach((subject) => {
        subject?.sections?.forEach((section) => {
          section?.questions?.forEach((question, qIndex) => {
            questions.push(question);
            questionMap[questions.length - 1] = {
              sectionId: section.id,
              subjectId: subject.id,
            };
          });
        });
      });

      return {
        questionData: questions,
        questionToSectionMap: questionMap,
        subjectNames: subjects,
        sectionNames: sections,
      };
    }, [question]);

  const [isOnSubmit, setIsOnSubmit] = useState(false);

  const subjectLengths = useMemo(
    () =>
      question?.subjects?.map((subject) => subject.subject_question_count) ||
      [],
    [question]
  );

  const sectionLengths = useMemo(() => {
    if (!question?.subjects) return [];

    const result = [];
    let cumulativeSum = 0;

    // First subject gets special treatment with starting 0
    const firstSubject = question.subjects[0];
    if (firstSubject?.sections) {
      const firstSubjectSections = [];
      for (const section of firstSubject.sections) {
        cumulativeSum += section.questions.length;
        firstSubjectSections.push(cumulativeSum);
      }
      result.push(firstSubjectSections);
    }

    // Remaining subjects
    for (let i = 1; i < question.subjects.length; i++) {
      const subject = question.subjects[i];
      const subjectSections = [];

      if (subject?.sections) {
        for (const section of subject.sections) {
          cumulativeSum += section.questions.length;
          subjectSections.push(cumulativeSum);
        }
      }

      result.push(subjectSections);
    }

    return result;
  }, [question]);

  useEffect(() => {
    if (subjectNames?.[0]?.subject_id) {
      setActiveSubjectId(subjectNames[0].subject_id);
    }
    if (sectionNames?.[0]?.[0]?.section_id) {
      setActiveSectionId(sectionNames[0][0].section_id);
    }
  }, [subjectNames, sectionNames]);

  useEffect(() => {
    if (subjectLengths.length > 0) {
      setCurrentSubject((prev) => [...prev, ...subjectLengths]);
    }
  }, [subjectLengths]);

  useEffect(() => {
    if (sectionLengths.length > 0) {
      setCurrentSection((prev) => [...prev, ...sectionLengths]);
    }
  }, [sectionLengths]);

  useEffect(() => {
    if (!questionData.length || !questionToSectionMap[currentQuestion]) return;

    const { sectionId, subjectId } = questionToSectionMap[currentQuestion];
    setSectionId(sectionId);
    setSubjectId(subjectId);
  }, [currentQuestion, questionData, questionToSectionMap]);

  useEffect(() => {
    const storedExamId = sessionStorage.getItem("examId");
    if (storedExamId) {
      fetchAttemptedQuestions(storedExamId);
    }
  }, []);

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const [reLoadIsOnsubmit, setReLoadIsOnsubmit] = useState(
    sessionStorage.getItem("isOnsubmit")
  );

  const handleRemove = () => {
    setIsOnSubmit(false);
    setReLoadIsOnsubmit(null);
    setMobileOpen(false);
    sessionStorage.removeItem("isOnsubmit");
    // Optionally reset other states or session storage items
    // setSscTimeUp(false);
    // sessionStorage.removeItem("submitByH");
  };

  useEffect(() => {
    socket.connect();
    const user = sessionStorage.getItem("userId");
    const exam = sessionStorage.getItem("examId");

    const SubmittedByHead = () => {
      sessionStorage.setItem("submitByH", true);
      sessionStorage.setItem("isOnsubmit", true);
      setIsOnSubmit(true);
      setSscTimeUp(true);
    };

    const handleExamSubmit = ({ examId, userId }) => {
      if (userId === user && examId === exam) SubmittedByHead();
    };

    const handleExamSubmitAll = ({ examId }) => {
      if (examId === exam) SubmittedByHead();
    };

    socket.on("exam_submit_check", handleExamSubmit);
    socket.on("exam_submit_check_all", handleExamSubmitAll);

    if (sessionStorage.getItem("submitByH")) {
      SubmittedByHead();
    }

    return () => {
      socket.off("exam_submit_check", handleExamSubmit);
      socket.off("exam_submit_check_all", handleExamSubmitAll);
      socket.disconnect();
    };
  }, []);

  const saveBeforeSubmitRef = useRef(null);

  return (
    <>
      <Box className={classes.rootContainer}>
        <Box
          style={{ display: isOnSubmit || reLoadIsOnsubmit ? "none" : "block" }}
        >
          {/* Header + Mobile Menu Icon */}
          <Header>
            <Box className={classes.headerContainer}>
              {isMobile && (
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={handleMobileDrawerToggle}
                  className={classes.menuButton}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Header>
        </Box>

        {isOnSubmit || reLoadIsOnsubmit ? (
          <SubmitSummary
            sectionNames={sectionNames}
            question={question}
            questionData={questionData}
            setIsOnSubmit={setIsOnSubmit}
            isOnSubmit={isOnSubmit}
            sscTimeUp={sscTimeUp}
            handleRemove={handleRemove}
            saveBeforeSubmitRef={saveBeforeSubmitRef}
          />
        ) : (
          <Box className={classes.mainContainer}>
            {/* Main content */}
            <Box className={classes.contentContainer}>
              <Box className={classes.questionContainer}>
                <QuestionSection
                  isMobile={isMobile}
                  setSscTimeUp={setSscTimeUp}
                  setIsOnSubmit={setIsOnSubmit}
                  subjectNames={subjectNames}
                  sectionNames={sectionNames}
                  currentSection={currentSection}
                  questionData={questionData}
                  currentQuestion={currentQuestion}
                  setCurrentQuestion={setCurrentQuestion}
                  subjectId={subjectId}
                  sectionId={sectionId}
                  currentSubject={currentSubject}
                  setActiveSubjectId={setActiveSubjectId}
                  setActiveSectionId={setActiveSectionId}
                  activeSectionId={activeSectionId}
                  activeSubjectId={activeSubjectId}
                  desktopOpen={desktopOpen}
                  saveBeforeSubmitRef={saveBeforeSubmitRef}
                  onTimerInitialized={setIsTimerInitialized}
                  isTimerInitialized={isTimerInitialized}
                />
              </Box>

              {/* Sidebar wrapper: pull-tab + panel (desktop only) */}
              <Box className={classes.sidebarWrapper}>
                {/* Pull-tab toggle: always visible */}
                <Box
                  className={classes.toggleBox}
                  onClick={handleDesktopToggle}
                >
                  <CollapseToggle
                    open={desktopOpen}
                    setOpen={handleDesktopToggle}
                  />
                </Box>

                {/* Sidebar panel: slides in/out */}
                <Box
                  className={classes.sidebarContainer}
                  sx={{
                    width: desktopOpen ? "300px" : "0px",
                    overflow: "hidden",
                    transition: "width 0.3s ease",
                  }}
                >
                  <QuestionSidebar
                    questionData={questionData}
                    currentQuestion={currentQuestion}
                    setCurrentQuestion={setCurrentQuestion}
                    sectionId={sectionId}
                    subjectId={subjectId}
                    activeSectionId={activeSectionId}
                    activeSubjectId={activeSubjectId}
                    setIsOnSubmit={setIsOnSubmit}
                  />
                </Box>
              </Box>
            </Box>

            {/* Sidebar Drawer (mobile only) */}
            <Drawer
              variant="temporary"
              anchor="right"
              open={mobileOpen}
              onClose={handleMobileDrawerToggle}
              ModalProps={{ keepMounted: true }}
              className={classes.mobileDrawer}
            >
              <QuestionSidebar
                questionData={questionData}
                currentQuestion={currentQuestion}
                setCurrentQuestion={setCurrentQuestion}
                sectionId={sectionId}
                subjectId={subjectId}
                activeSectionId={activeSectionId}
                activeSubjectId={activeSubjectId}
                setIsOnSubmit={setIsOnSubmit}
                mobileOpen={mobileOpen}
                isMobile={isMobile}
                onClose={handleMobileDrawerToggle}
              />
            </Drawer>
          </Box>
        )}
      </Box>
    </>
  );
}

export default GateTest;
